import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { formatMedida } from "@/lib/format";
import { sharePdf } from "@/lib/share";
import BarcodeScanner from "@/components/BarcodeScanner";
import { beepSuccess, beepDuplicate, beepError } from "@/lib/sound";
import { Camera, CameraOff, Search, X, ScanLine, Loader2, Plus, Send, Printer, Truck, CheckCircle2, ArrowLeft, List, Share2 } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "h-12 w-full rounded-md border-2 border-slate-300 px-3 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

export default function Romaneio() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("barcode"); // barcode | manual
  const [cameraOn, setCameraOn] = useState(false);
  const [manualCode, setManualCode] = useState("");

  const [selected, setSelected] = useState([]); // [{...rolo, bloco}]
  const [ribanaAtiva, setRibanaAtiva] = useState(false);
  const [activeBloco, setActiveBloco] = useState("principal");

  // busca manual
  const [combinacoes, setCombinacoes] = useState([]);
  const [comb, setComb] = useState("");
  const [available, setAvailable] = useState([]);

  // destino
  const [destinos, setDestinos] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [destino, setDestino] = useState("");
  const [novoDestino, setNovoDestino] = useState("");
  const [ref, setRef] = useState("");
  const [cliente, setCliente] = useState("");
  const [descricaoPeca, setDescricaoPeca] = useState("");
  const [observacao, setObservacao] = useState("");
  const [saving, setSaving] = useState(false);

  const [resultado, setResultado] = useState(null);

  const codeInputRef = useRef(null);

  useEffect(() => {
    api.get("/combinacoes").then((r) => setCombinacoes(r.data)).catch(() => {});
    api.get("/destinos").then((r) => setDestinos(r.data)).catch(() => {});
    api.get("/fornecedores").then((r) => setFornecedores(r.data)).catch(() => {});
  }, []);

  const abrirPdf = (rom) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/romaneios/${rom.id}/pdf`, "_blank");
  };

  const addRoloByCodigo = useCallback(
    async (codigo, bloco) => {
      const code = String(codigo).trim().toUpperCase();
      if (!code) return;
      if (selected.some((r) => r.codigo === code)) {
        beepDuplicate();
        toast.info(`${code} já está no romaneio`);
        return;
      }
      try {
        const res = await api.get(`/rolos/codigo/${code}`);
        const rolo = res.data;
        if (rolo.status !== "Disponível") {
          beepError();
          toast.error(`${rolo.codigo} não está disponível (${rolo.status})`);
          return;
        }
        setSelected((prev) =>
          prev.some((r) => r.id === rolo.id) ? prev : [...prev, { ...rolo, bloco }]
        );
        beepSuccess();
        toast.success(
          `${rolo.codigo} — ${rolo.tipo} ${rolo.cor} — ${formatMedida(rolo.peso_atual_kg, rolo.unidade)} (${
            bloco === "ribana" ? "Ribana" : "Principal"
          })`
        );
      } catch (err) {
        beepError();
        {const _m=apiError(err); if(_m) toast.error(_m);};
      }
    },
    [selected]
  );

  const onManualKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addRoloByCodigo(manualCode, activeBloco);
      setManualCode("");
    }
  };

  const loadAvailable = async (cid) => {
    setComb(cid);
    if (!cid) {
      setAvailable([]);
      return;
    }
    try {
      const res = await api.get(`/combinacoes/${cid}/rolos`, { params: { status: "Disponível" } });
      setAvailable(res.data);
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    }
  };

  const addFromList = (rolo) =>
    setSelected((prev) => (prev.some((r) => r.id === rolo.id) ? prev : [...prev, { ...rolo, bloco: activeBloco }]));

  const removeSelected = (id) => setSelected((prev) => prev.filter((r) => r.id !== id));

  const toggleRibana = () => {
    setRibanaAtiva((v) => {
      const next = !v;
      if (!next) {
        setSelected((prev) => prev.filter((r) => r.bloco !== "ribana"));
        setActiveBloco("principal");
      }
      return next;
    });
  };

  const addDestino = async () => {
    if (!novoDestino.trim()) return;
    try {
      const res = await api.post("/destinos", { nome: novoDestino.trim() });
      setDestinos(res.data);
      setDestino(novoDestino.trim());
      setNovoDestino("");
      toast.success("Destino adicionado");
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    }
  };

  const confirmar = async () => {
    if (selected.length === 0) return toast.error("Bipe ou selecione ao menos um rolo");
    if (!ref.trim()) return toast.error("Informe a referência (REF)");
    if (!destino) return toast.error("Selecione o destino");
    setSaving(true);
    try {
      const res = await api.post("/romaneios", {
        ref: ref.trim(),
        destino,
        cliente: cliente.trim() || null,
        descricao_peca: descricaoPeca.trim() || null,
        observacao: observacao.trim() || null,
        itens: selected.map((r) => ({ rolo_id: r.id, bloco: r.bloco })),
      });
      setResultado(res.data);
      toast.success(`Romaneio ${res.data.codigo} gerado — ${res.data.total_rolos} rolo(s)`);
      setSelected([]);
      setRef("");
      setCliente("");
      setDescricaoPeca("");
      setObservacao("");
      setRibanaAtiva(false);
      setActiveBloco("principal");
      if (comb) loadAvailable(comb);
    } catch (err) {
      {const _m=apiError(err); if(_m) toast.error(_m);};
    } finally {
      setSaving(false);
    }
  };

  const principalSel = selected.filter((r) => r.bloco === "principal");
  const ribanaSel = selected.filter((r) => r.bloco === "ribana");

  const BlocoPill = ({ value, label }) => (
    <button
      onClick={() => setActiveBloco(value)}
      disabled={value === "ribana" && !ribanaAtiva}
      data-testid={`bloco-${value}`}
      className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        activeBloco === value ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      }`}
    >
      {label}
    </button>
  );

  const renderList = (arr, titulo, testid) => (
    <div className="mt-3">
      <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">{titulo}</div>
      <ul className="divide-y divide-slate-100 rounded-md border-2 border-slate-100" data-testid={testid}>
        {arr.length === 0 && <li className="py-4 text-center text-sm text-slate-400">Nenhum rolo.</li>}
        {arr.map((r) => (
          <li key={r.id} className="flex items-center justify-between px-3 py-2.5" data-testid={`sel-${r.codigo}`}>
            <div>
              <span className="font-mono font-semibold text-slate-900">{r.codigo}</span>
              <div className="text-xs text-slate-500">
                {r.tipo} {r.cor} · {r.fornecedor_nome} · {formatMedida(r.peso_atual_kg, r.unidade)}
              </div>
            </div>
            <button
              onClick={() => removeSelected(r.id)}
              data-testid={`remove-${r.codigo}`}
              className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-red-600"
            >
              <X size={18} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div data-testid="romaneio-page">
      <div className="mb-6">
        <button
          onClick={() => navigate("/romaneio")}
          data-testid="romaneio-voltar"
          className="mb-2 flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft size={16} /> Voltar para a lista
        </button>
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
          <Truck size={28} /> Criar romaneio de envio
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Bipe os rolos para montar o documento de envio. Vincule a uma REF e ao destino e gere o PDF (A4 retrato, 2 vias).
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Captura */}
        <section className="rounded-md border-2 border-slate-200 bg-white p-5">
          {/* Pergunta Ribana */}
          <div className="mb-4 flex items-center justify-between rounded-md bg-amber-50 px-4 py-3">
            <span className="text-sm font-semibold text-amber-900">Este romaneio tem ribana?</span>
            <div className="flex gap-2">
              <button
                onClick={() => !ribanaAtiva && toggleRibana()}
                data-testid="ribana-sim"
                className={`rounded-md px-4 py-1.5 text-sm font-semibold ${
                  ribanaAtiva ? "bg-amber-600 text-white" : "bg-white text-amber-700 border-2 border-amber-300"
                }`}
              >
                Sim
              </button>
              <button
                onClick={() => ribanaAtiva && toggleRibana()}
                data-testid="ribana-nao"
                className={`rounded-md px-4 py-1.5 text-sm font-semibold ${
                  !ribanaAtiva ? "bg-amber-600 text-white" : "bg-white text-amber-700 border-2 border-amber-300"
                }`}
              >
                Não
              </button>
            </div>
          </div>

          {/* Seletor de bloco */}
          {ribanaAtiva && (
            <div className="mb-4">
              <div className="mb-1 text-xs font-semibold text-slate-600">Bipando para o bloco:</div>
              <div className="flex gap-2">
                <BlocoPill value="principal" label="Tecido principal" />
                <BlocoPill value="ribana" label="Ribana" />
              </div>
            </div>
          )}

          {/* Modo */}
          <div className="mb-4 flex gap-2">
            <button
              onClick={() => setMode("barcode")}
              data-testid="mode-barcode"
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold ${
                mode === "barcode" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
              }`}
            >
              <ScanLine size={16} /> Código de barras
            </button>
            <button
              onClick={() => setMode("manual")}
              data-testid="mode-manual"
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold ${
                mode === "manual" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
              }`}
            >
              <Search size={16} /> Buscar manualmente
            </button>
          </div>

          {mode === "barcode" ? (
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Código do rolo (leitor USB ou digitar)
              </label>
              <input
                ref={codeInputRef}
                autoFocus
                className={`${inputCls} font-mono`}
                placeholder="ROLO-0001 e Enter"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                onKeyDown={onManualKey}
                data-testid="romaneio-code-input"
              />
              <button
                onClick={() => setCameraOn((v) => !v)}
                data-testid="toggle-camera"
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border-2 border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                {cameraOn ? <CameraOff size={16} /> : <Camera size={16} />}
                {cameraOn ? "Fechar câmera" : "Abrir câmera do celular"}
              </button>
              {cameraOn && (
                <div className="mt-3">
                  <BarcodeScanner onScan={(t) => addRoloByCodigo(t, activeBloco)} />
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Tecido</label>
              <select
                className={inputCls}
                value={comb}
                onChange={(e) => loadAvailable(e.target.value)}
                data-testid="romaneio-combinacao"
              >
                <option value="">Selecione o tecido</option>
                {combinacoes
                  .slice()
                  .sort((a, b) =>
                    `${a.tipo} ${a.cor} ${a.fornecedor_nome}`.localeCompare(
                      `${b.tipo} ${b.cor} ${b.fornecedor_nome}`,
                      "pt-BR"
                    )
                  )
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.tipo} {c.cor} — {c.fornecedor_nome}
                    </option>
                  ))}
              </select>
              <div className="mt-3 max-h-72 overflow-y-auto" data-testid="romaneio-available-list">
                {comb && available.length === 0 && (
                  <p className="py-4 text-center text-sm text-slate-400">Nenhum rolo disponível.</p>
                )}
                <ul className="divide-y divide-slate-100">
                  {available.map((r) => {
                    const inList = selected.some((s) => s.id === r.id);
                    return (
                      <li key={r.id} className="flex items-center justify-between py-2.5">
                        <div>
                          <span className="font-mono font-semibold text-slate-900">{r.codigo}</span>
                          <span className="ml-2 font-mono text-sm text-slate-500">
                            {formatMedida(r.peso_atual_kg, r.unidade)}
                          </span>
                        </div>
                        <button
                          disabled={inList}
                          onClick={() => addFromList(r)}
                          data-testid={`add-rolo-${r.codigo}`}
                          className="flex items-center gap-1 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                        >
                          <Plus size={14} /> {inList ? "Adicionado" : "Adicionar"}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </section>

        {/* Lista + confirmação */}
        <section className="rounded-md border-2 border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-900">Itens do romaneio</h2>
            <span
              className="rounded-sm bg-slate-100 px-2 py-1 text-sm font-bold text-slate-700"
              data-testid="romaneio-count"
            >
              {selected.length} rolo(s)
            </span>
          </div>

          {renderList(principalSel, "Tecido principal", "lista-principal")}
          {ribanaAtiva && renderList(ribanaSel, "Ribana", "lista-ribana")}

          <div className="mt-4 space-y-3 border-t-2 border-slate-100 pt-4">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Referência (REF)</label>
              <input
                className={inputCls}
                placeholder="ex: REF 3364"
                value={ref}
                onChange={(e) => setRef(e.target.value)}
                data-testid="romaneio-ref"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Cliente</label>
              <input
                className={inputCls}
                placeholder="ex: João Modas"
                value={cliente}
                onChange={(e) => setCliente(e.target.value)}
                data-testid="romaneio-cliente"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Descrição da peça</label>
              <input
                className={inputCls}
                placeholder="ex: Moletom Canguru Recortado e Estampado"
                value={descricaoPeca}
                onChange={(e) => setDescricaoPeca(e.target.value)}
                data-testid="romaneio-descricao"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Destino</label>
              <select
                className={inputCls}
                value={destino}
                onChange={(e) => setDestino(e.target.value)}
                data-testid="romaneio-destino"
              >
                <option value="">Selecione o destino</option>
                <optgroup label="Internos / Faccionistas">
                  {destinos.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </optgroup>
                {fornecedores.length > 0 && (
                  <optgroup label="Fornecedores (devolução de tecido)">
                    {fornecedores.map((f) => (
                      <option key={f.id} value={`${f.nome} (fornecedor)`}>
                        {f.nome}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <div className="mt-2 flex gap-2">
                <input
                  className="h-10 flex-1 rounded-md border-2 border-slate-200 px-3 text-sm outline-none focus:border-blue-500"
                  placeholder="Outro destino (livre)"
                  value={novoDestino}
                  onChange={(e) => setNovoDestino(e.target.value)}
                  data-testid="romaneio-novo-destino"
                />
                <button
                  onClick={addDestino}
                  data-testid="romaneio-add-destino"
                  className="rounded-md border-2 border-slate-200 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Observação (opcional)</label>
              <input
                className={inputCls}
                placeholder="ex: defeito, devolução parcial..."
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                data-testid="romaneio-obs"
              />
            </div>

            <button
              onClick={confirmar}
              disabled={saving}
              data-testid="romaneio-confirm"
              className="flex h-14 w-full items-center justify-center gap-2 rounded-md bg-slate-900 text-base font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
              Gerar romaneio
            </button>
          </div>
        </section>
      </div>

      {/* Resultado */}
      {resultado && (
        <section
          className="mt-6 rounded-md border-2 border-green-200 bg-green-50 p-5"
          data-testid="romaneio-resultado"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="text-green-600" size={28} />
              <div>
                <div className="font-mono text-xl font-bold text-slate-900">{resultado.codigo}</div>
                <div className="text-sm text-slate-600">
                  REF {resultado.ref} · {resultado.destino} · {resultado.total_rolos} rolo(s)
                </div>
              </div>
            </div>
            <button
              onClick={() => abrirPdf(resultado)}
              data-testid="romaneio-print"
              className="flex items-center gap-2 rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800"
            >
              <Printer size={18} /> Imprimir Romaneio (PDF A4)
            </button>
            <button
              onClick={() => sharePdf(`/romaneios/${resultado.id}/pdf`, `Romaneio - REF ${resultado.ref}.pdf`, `Romaneio REF ${resultado.ref}`)}
              data-testid="romaneio-share"
              className="flex items-center gap-2 rounded-md bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <Share2 size={18} /> Compartilhar
            </button>
            <button
              onClick={() => navigate("/romaneio")}
              data-testid="romaneio-ir-lista"
              className="flex items-center gap-2 rounded-md border-2 border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            >
              <List size={18} /> Ver lista de romaneios
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
